import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  Icon,
  alpha,
  useTheme,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { mockCOAMappings } from "../../mock.data";

interface ConfigurationModule {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  route: string;
  completionPercentage: number;
  totalItems: number;
  configuredItems: number;
  status: "complete" | "partial" | "not_started";
  lastUpdated?: string;
}

export const ConfigurationDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Calculate COA Mappings completion
  const coaMappingsStats = {
    total: mockCOAMappings.length,
    configured: mockCOAMappings.filter(
      (m) => m.debit_account_id && m.credit_account_id
    ).length,
    active: mockCOAMappings.filter(
      (m) => m.is_active && m.debit_account_id && m.credit_account_id
    ).length,
  };

  const configurationModules: ConfigurationModule[] = [
    {
      id: "coa-mappings",
      title: "COA Mappings",
      description: "Account mappings for automated journal generation",
      icon: "account_tree",
      color: "#10B981",
      route: "/accounting/configuration/coa-mappings",
      completionPercentage: Math.round(
        (coaMappingsStats.configured / coaMappingsStats.total) * 100
      ),
      totalItems: coaMappingsStats.total,
      configuredItems: coaMappingsStats.configured,
      status:
        coaMappingsStats.configured === coaMappingsStats.total
          ? "complete"
          : coaMappingsStats.configured > 0
          ? "partial"
          : "not_started",
      lastUpdated: "2024-03-20",
    },
    {
      id: "voucher-settings",
      title: "Voucher Settings",
      description: "Voucher types, prefixes, and validation rules",
      icon: "receipt",
      color: "#F59E0B",
      route: "/accounting/configuration/voucher-settings",
      completionPercentage: 85,
      totalItems: 12,
      configuredItems: 10,
      status: "partial",
      lastUpdated: "2024-03-18",
    },
    {
      id: "journal-settings",
      title: "Journal Settings",
      description: "Journal entry rules and audit settings",
      icon: "book",
      color: "#8B5CF6",
      route: "/accounting/configuration/journal-settings",
      completionPercentage: 100,
      totalItems: 8,
      configuredItems: 8,
      status: "complete",
      lastUpdated: "2024-03-15",
    },
    {
      id: "ledger-settings",
      title: "Ledger Settings",
      description: "Ledger display and export options",
      icon: "table_rows",
      color: "#06B6D4",
      route: "/accounting/configuration/ledger-settings",
      completionPercentage: 75,
      totalItems: 6,
      configuredItems: 4,
      status: "partial",
      lastUpdated: "2024-03-10",
    },
    {
      id: "permissions",
      title: "User Permissions",
      description: "User roles and access control matrix",
      icon: "security",
      color: "#EF4444",
      route: "/accounting/configuration/permissions",
      completionPercentage: 60,
      totalItems: 15,
      configuredItems: 9,
      status: "partial",
      lastUpdated: "2024-03-05",
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "complete":
        return "#10B981";
      case "partial":
        return "#F59E0B";
      case "not_started":
        return "#EF4444";
      default:
        return "#6B7280";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "complete":
        return "Complete";
      case "partial":
        return "In Progress";
      case "not_started":
        return "Not Started";
      default:
        return "Unknown";
    }
  };

  const overallCompletion = Math.round(
    configurationModules.reduce(
      (acc, module) => acc + module.completionPercentage,
      0
    ) / configurationModules.length
  );

  const handleModuleClick = (route: string) => {
    navigate(route);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Configuration Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Monitor and manage your accounting system configuration
        </Typography>
      </Box>

      {/* Overall Progress */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ fontSize: 28, color: "primary.main" }}>settings</Icon>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Overall Configuration Progress
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {overallCompletion}% of configuration modules completed
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                variant="h3"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                {overallCompletion}%
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={overallCompletion}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Configuration Modules */}
      <Grid container spacing={3}>
        {configurationModules.map((module) => (
          <Grid item xs={12} md={6} lg={4} key={module.id}>
            <Card
              sx={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[8],
                },
              }}
              onClick={() => handleModuleClick(module.route)}
            >
              <CardContent>
                {/* Header */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: alpha(module.color, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ fontSize: 20, color: module.color }}>
                      {module.icon}
                    </Icon>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {module.title}
                    </Typography>
                    <Chip
                      size="small"
                      label={getStatusLabel(module.status)}
                      sx={{
                        bgcolor: alpha(getStatusColor(module.status), 0.1),
                        color: getStatusColor(module.status),
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 3, lineHeight: 1.5 }}
                >
                  {module.description}
                </Typography>

                {/* Progress */}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Progress
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {module.configuredItems}/{module.totalItems} configured
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={module.completionPercentage}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(module.color, 0.1),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                        bgcolor: module.color,
                      },
                    }}
                  />
                </Box>

                {/* Stats */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: module.color }}
                  >
                    {module.completionPercentage}%
                  </Typography>
                  {module.lastUpdated && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Updated {module.lastUpdated}
                    </Typography>
                  )}
                </Box>

                {/* Action Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    mt: 2,
                    borderColor: alpha(module.color, 0.3),
                    color: module.color,
                    "&:hover": {
                      borderColor: module.color,
                      bgcolor: alpha(module.color, 0.05),
                    },
                  }}
                  endIcon={<Icon>arrow_forward</Icon>}
                >
                  Configure
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Icon>download</Icon>}
                sx={{ py: 1.5 }}
              >
                Export Configuration
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Icon>upload</Icon>}
                sx={{ py: 1.5 }}
              >
                Import Configuration
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Icon>backup</Icon>}
                sx={{ py: 1.5 }}
              >
                Backup Settings
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Icon>restore</Icon>}
                sx={{ py: 1.5 }}
              >
                Restore Defaults
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
